/* React */
import React, { useRef, useEffect, useId } from "react";

/* React Redux */
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";

/* d3.js*/
import * as d3 from "d3";

/* Components */
import ChartTooltip from "src/components/shared/chart-tooltip/ChartTooltip";
import EmptyChartContainer from "src/components/charts/empty-chart-container/EmptyChartContainer";

/* Utils */
import { createBoxplot } from "src/components/charts/horizontal-boxplot/horizontalBoxplotRenderer";

/* Stylesheet */
import styles from "./HorizontalBoxplot.module.scss";

/* Interfaces */
import { ChartTooltipRef } from "src/shared/interfaces/ChartTooltip";
import { BoxplotProps } from "src/shared/interfaces/Boxplot";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";

interface HorizontalBoxplotProps {
  data: BoxplotProps[];
  dimension: ChartDimensionsProps;
}

const HorizontalBoxplot: React.FC<
  HorizontalBoxplotProps
> = ({ data, dimension }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tooltipRef = useRef<ChartTooltipRef>(null);

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { customFeature } = insightsFilterState;

  const uniqueId = useId();
  const gradientPrefix = `boxplot-${uniqueId}`;

  const searchTermState = useSelector(
    (state: RootState) => state.searchTerm
  );

  const { searchTerm } = searchTermState;

  const filteredData = searchTerm
    ? data.filter((data) => {
        return data.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      })
    : data;

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    createBoxplot(
      svg,
      filteredData,
      dimension,
      tooltipRef,
      gradientPrefix,
      customFeature?.value
    );
  }, [
    filteredData,
    dimension,
    gradientPrefix,
    customFeature?.value,
  ]);

  return (
    <div className={styles.horizontal_boxplot}>
      <ChartTooltip ref={tooltipRef} />
      {data.length > 0 ? (
        <svg ref={svgRef} width={dimension.width} />
      ) : (
        <EmptyChartContainer
          scssProps={{
            height: `${dimension.height + 24}px`,
          }}
        />
      )}
    </div>
  );
};

export default HorizontalBoxplot;
